import type { UploadInstructions } from "../api/types";

const PREFIX = "deepvision.session-upload.v1.";

export interface UploadedPartState {
  partNumber: number;
  etag: string;
  size: number;
}

export interface UploadResumeState {
  version: 2;
  sessionId: string;
  organizationId: string;
  projectId: string;
  instructions: UploadInstructions;
  completedFiles: string[];
  parts: Record<string, UploadedPartState[]>;
  updatedAt: string;
}

export function createResumeState(sessionId: string, organizationId: string, projectId: string, instructions: UploadInstructions): UploadResumeState {
  return { version: 2, sessionId, organizationId, projectId, instructions, completedFiles: [], parts: {}, updatedAt: new Date().toISOString() };
}

export function loadResumeState(sessionId: string, organizationId: string, projectId: string): UploadResumeState | null {
  try {
    const value = localStorage.getItem(storageKey(sessionId, organizationId, projectId));
    if (!value) return null;
    const parsed = JSON.parse(value) as UploadResumeState;
    return isValidState(parsed, sessionId, organizationId, projectId) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveResumeState(state: UploadResumeState): void {
  state.updatedAt = new Date().toISOString();
  try { localStorage.setItem(storageKey(state.sessionId, state.organizationId, state.projectId), JSON.stringify(state)); } catch { /* 隐私模式下仍允许当前页上传。 */ }
}

export function clearResumeState(sessionId: string, organizationId: string, projectId: string): void {
  try { localStorage.removeItem(storageKey(sessionId, organizationId, projectId)); } catch { /* 忽略不可用的本地存储。 */ }
}

function storageKey(sessionId: string, organizationId: string, projectId: string): string {
  return `${PREFIX}${organizationId}.${projectId}.${sessionId}`;
}

function isValidState(value: UploadResumeState, sessionId: string, organizationId: string, projectId: string): boolean {
  if (!value || value.version !== 2 || value.sessionId !== sessionId || value.organizationId !== organizationId || value.projectId !== projectId) return false;
  if (!Array.isArray(value.completedFiles) || !value.parts || typeof value.parts !== "object") return false;
  return value.instructions?.strategy === "local_put" || value.instructions?.strategy === "oss_multipart";
}
